/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * collections.ts - Utility functions for working with collections
 */

/**
 * Produces a range from `from` to `to` inclusive.
 * 
 * Note that the step size should divide the difference between `to` and `from`. If it doesn't, the last value in the
 * range will be the last step without going past `to`. E.g. range(0, 5, 2) gives [0, 2, 4].
 * 
 * Accepts non-integers, but be weary.
 * 
 * @param from The first number in the array.
 * @param to The last number in the array. Allowed to be greater, equal, or less than `to`.
 * @param step The step size (absolute value). Should divide abs(to-from).
 * @returns Array of numbers from `from` to `to` inclusive.
 */
export function range(from: number, to: number, step = 1): number[] {
  if (step < 0) { throw new Error(`step size ${step} was not positive`); }
  if (to === from) { return [from]; }
  
  const signedStep = (to > from) ? step : -step;
  const finishedCondition: (x: number) => boolean = (to > from) ? x => x > to : x => x < to;

  const array = [];
  let x = from;

  while (!finishedCondition(x)) {
    array.push(x);
    x += signedStep;  
  }
  
  return array;
}

/** Produce a filled array of the given length, initialized by the given */
export function filledArray<T>(x: T, length: number): T[] {
  return Array(length).fill(x);
}

/** 
 * Get a random element from an array, leaving the array unchanged.
 * Returns null if the input array is empty.
 */
export function getRandomFromArray<T>(input: readonly T[]): T | null {
  if (input.length === 0) { return null; }
  const choice = Math.floor(Math.random() * input.length);
  return input[choice];
}

/** Type to represent an item with a weight. */
export type Weighted<T> = { item: T, weight: number };

/**
 * Like {@link getRandomFromArray} except that weights are provided and then used when picking the random number.
 * Returns null if the input array is empty.
 */
export function getRandomFromArrayWithWeights<T>(
  input: readonly Weighted<T>[]
): T | null {
  if (input.length === 0) { return null; }
  const totalWeight = input.reduce((total, { weight }) => total + weight, 0);
  const choice = Math.random() * totalWeight;

  let cumulativeWeight = 0;
  for (const { item, weight } of input) {
    cumulativeWeight += weight;
    if (cumulativeWeight >= choice) {
      return item;
    }
  }

  throw Error(`Logic error: choice ${choice} was greater than total weight ${cumulativeWeight}`);
}

/**
 * Take a random element from an array, removing it from the array.
 * Returns null if the input array is empty. 
 */
export function removeRandomFromArray<T>(input: T[]): T | null {
  if (input.length === 0) { return null; }
  const choice = Math.floor(Math.random() * input.length);
  return input.splice(choice, 1)[0];
}
